"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.gasEstimateResponseDummyProofIterativeRelayerFee = exports.calculateRelayerFeeERC20Amount = void 0;
const shared_models_1 = require("@railgun-community/shared-models");
const tx_generator_1 = require("./tx-generator");
const tx_gas_details_1 = require("./tx-gas-details");
const balance_update_1 = require("../railgun/wallets/balance-update");
const railgun_1 = require("../railgun");
const commitment_1 = require("../railgun/util/commitment");
const MAX_ITERATIONS_RELAYER_FEE_REESTIMATION = 5;
const calculateRelayerFeeERC20Amount = (feeTokenDetails, gasDetails) => {
    const tokenFeePerUnitGas = BigInt(feeTokenDetails.feePerUnitGas);
    const oneUnitGas = 10n ** 18n;
    const maximumGas = (0, shared_models_1.calculateMaximumGas)(gasDetails);
    const tokenFee = (tokenFeePerUnitGas * maximumGas) / oneUnitGas;
    return {
        tokenAddress: feeTokenDetails.tokenAddress,
        amount: tokenFee,
    };
};
exports.calculateRelayerFeeERC20Amount = calculateRelayerFeeERC20Amount;
const getRelayerFeeCommitment = (transactionStructs) => {
    const transactionIndex = 0;
    const relayerFeeCommitment = transactionStructs[transactionIndex];
    const relayerFeeCommitmentIndex = 0;
    return (0, commitment_1.convertTransactionStructToCommitmentSummary)(relayerFeeCommitment, relayerFeeCommitmentIndex);
};
const gasEstimateResponseDummyProofIterativeRelayerFee = async (generateDummyTransactionStructsWithRelayerFee, generateTransaction, networkName, railgunWalletID, erc20AmountRecipients, originalGasDetails, feeTokenDetails, sendWithPublicWallet, isCrossContractCall) => {
    const wallet = (0, railgun_1.walletForID)(railgunWalletID);
    // Use dead address for private transaction gas estimate
    const fromWalletAddress = tx_generator_1.DUMMY_FROM_ADDRESS;
    const isGasEstimateWithDummyProof = true;
    const dummyRelayerFee = feeTokenDetails
        ? (0, tx_generator_1.createDummyRelayerFeeERC20Amount)(feeTokenDetails.tokenAddress)
        : undefined;
    let serializedTransactions = await generateDummyTransactionStructsWithRelayerFee(dummyRelayerFee);
    let transaction = await generateTransaction(serializedTransactions);
    let gasEstimate = await (0, tx_gas_details_1.getGasEstimate)(networkName, transaction, fromWalletAddress, sendWithPublicWallet, isCrossContractCall);
    if (sendWithPublicWallet) {
        return (0, tx_gas_details_1.gasEstimateResponse)(gasEstimate, undefined, // relayerFeeCommitment
        isGasEstimateWithDummyProof);
    }
    if (!feeTokenDetails) {
        throw new Error('Must have Relayer Fee details or sendWithPublicWallet field.');
    }
    // Find any erc20Amount in transfer that matches token of relayer fee, if exists.
    const relayerFeeMatchingSendingERC20Amount = erc20AmountRecipients.find(erc20AmountRecipient => erc20AmountRecipient.tokenAddress.toLowerCase() ===
        feeTokenDetails.tokenAddress.toLowerCase());
    // Get private balance of matching token.
    const balanceForRelayerFeeERC20 = await (0, balance_update_1.balanceForERC20Token)(wallet, networkName, feeTokenDetails.tokenAddress);
    let relayerFeeCommitment = getRelayerFeeCommitment(serializedTransactions);
    // Iteratively calculate new relayer fee and estimate new gas amount.
    // This change if the number of circuits changes because of the additional Relayer Fees.
    for (let i = 0; i < MAX_ITERATIONS_RELAYER_FEE_REESTIMATION; i += 1) {
        const updatedGasDetails = {
            ...originalGasDetails,
            gasEstimate,
        };
        const updatedRelayerFee = (0, exports.calculateRelayerFeeERC20Amount)(feeTokenDetails, updatedGasDetails);
        // If Relayer fee causes overflow with the token balance,
        // then use the MAX amount for Relayer Fee, which is BALANCE - SENDING AMOUNT.
        if (balanceForRelayerFeeERC20 > 0n &&
            relayerFeeMatchingSendingERC20Amount &&
            // eslint-disable-next-line no-await-in-loop
            (await relayerFeeWillOverflowBalance(balanceForRelayerFeeERC20, relayerFeeMatchingSendingERC20Amount, updatedRelayerFee))) {
            updatedRelayerFee.amount =
                balanceForRelayerFeeERC20 - relayerFeeMatchingSendingERC20Amount.amount;
        }
        const newSerializedTransactions = 
        // eslint-disable-next-line no-await-in-loop
        await generateDummyTransactionStructsWithRelayerFee(updatedRelayerFee);
        relayerFeeCommitment = getRelayerFeeCommitment(newSerializedTransactions);
        if (compareCircuitSizesTransactionStructs(newSerializedTransactions, serializedTransactions)) {
            // Same circuit sizes, no need to run further gas estimates.
            return (0, tx_gas_details_1.gasEstimateResponse)(gasEstimate, relayerFeeCommitment, isGasEstimateWithDummyProof);
        }
        serializedTransactions = newSerializedTransactions;
        // eslint-disable-next-line no-await-in-loop
        transaction = await generateTransaction(serializedTransactions);
        // eslint-disable-next-line no-await-in-loop
        const newGasEstimate = await (0, tx_gas_details_1.getGasEstimate)(networkName, transaction, fromWalletAddress, sendWithPublicWallet, isCrossContractCall);
        if (newGasEstimate === gasEstimate) {
            return (0, tx_gas_details_1.gasEstimateResponse)(newGasEstimate, relayerFeeCommitment, isGasEstimateWithDummyProof);
        }
        gasEstimate = newGasEstimate;
    }
    return (0, tx_gas_details_1.gasEstimateResponse)(gasEstimate, relayerFeeCommitment, isGasEstimateWithDummyProof);
};
exports.gasEstimateResponseDummyProofIterativeRelayerFee = gasEstimateResponseDummyProofIterativeRelayerFee;
const compareCircuitSizesTransactionStructs = (serializedA, serializedB) => {
    if (serializedA.length !== serializedB.length) {
        return false;
    }
    for (let i = 0; i < serializedA.length; i += 1) {
        if (serializedA[i].commitments.length !== serializedB[i].commitments.length) {
            return false;
        }
        if (serializedA[i].nullifiers.length !== serializedB[i].nullifiers.length) {
            return false;
        }
    }
    return true;
};
const relayerFeeWillOverflowBalance = async (tokenBalance, sendingERC20Amount, relayerFeeERC20Amount) => {
    const sendingAmount = sendingERC20Amount.amount;
    const relayerFeeAmount = relayerFeeERC20Amount.amount;
    return sendingAmount + relayerFeeAmount > tokenBalance;
};
